import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import PhoneInput from '../components/PhoneInput';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { PhoneCall, Loader2, CheckCircle2, Clock, AlertTriangle, Flame, Sun, Sunrise, Sunset, Scale, Calculator, HelpCircle, Paperclip, FileText, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = 'w-full px-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm';
const label = 'block text-[#1B3A5C] text-xs tracking-wider font-medium mb-2';

const ZEITEN = [
  { key: 'frueh', icon: Sunrise, title: 'rueckruf.zeiten.frueh.title', desc: 'rueckruf.zeiten.frueh.desc' },
  { key: 'spaet_vormittag', icon: Sun, title: 'rueckruf.zeiten.spaet_vormittag.title', desc: 'rueckruf.zeiten.spaet_vormittag.desc' },
  { key: 'nachmittag', icon: Sunset, title: 'rueckruf.zeiten.nachmittag.title', desc: 'rueckruf.zeiten.nachmittag.desc' },
];
const BEREICHE = [
  { key: 'recht', icon: Scale, title: 'rueckruf.bereiche.recht.title', desc: 'rueckruf.bereiche.recht.desc' },
  { key: 'steuern', icon: Calculator, title: 'rueckruf.bereiche.steuern.title', desc: 'rueckruf.bereiche.steuern.desc' },
  { key: 'beides', icon: HelpCircle, title: 'rueckruf.bereiche.beides.title', desc: 'rueckruf.bereiche.beides.desc' },
];
const TEL_LINK = { lnk: <a href="tel:+492283040640" className="text-[#b8893d] underline" /> };
const TEL_LINK_PLAIN = { lnk: <a href="tel:+492283040640" className="underline font-medium" /> };
const DATENSCHUTZ_LINK = { lnk: <Link to="/datenschutz" target="_blank" className="text-[#b8893d] underline" /> };
const DATEI_EXTS = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp', '.doc', '.docx'];
const DRINGLICHKEIT = [
  { key: 'normal', icon: Clock, title: 'rueckruf.dringlichkeit.normal.title', desc: 'rueckruf.dringlichkeit.normal.desc' },
  { key: 'dringend', icon: AlertTriangle, title: 'rueckruf.dringlichkeit.dringend.title', desc: 'rueckruf.dringlichkeit.dringend.desc' },
  { key: 'sehr_dringend', icon: Flame, title: 'rueckruf.dringlichkeit.sehr_dringend.title', desc: 'rueckruf.dringlichkeit.sehr_dringend.desc' },
];

const TileGroup = ({ items, selected, onPick, testid, multi }) => {
  const { t } = useTranslation();
  return (
    <div className={`grid grid-cols-2 ${items.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-3`} data-testid={testid}>
      {items.map((it) => {
        const active = multi ? selected.includes(it.key) : selected === it.key;
        return (
          <button key={it.key} type="button" onClick={() => onPick(it.key)} data-testid={`${testid}-${it.key}`} aria-pressed={active} className={`text-left p-3 border-2 rounded-sm transition-colors ${active ? 'border-[#b8893d] bg-[#fbf7f0]' : 'border-gray-200 hover:border-[#b8893d]/50 bg-white'}`}>
            <it.icon className={`w-4 h-4 mb-1.5 ${active ? 'text-[#b8893d]' : 'text-[#1B3A5C]'}`} />
            <div className="text-sm font-medium text-[#1B3A5C]">{t(it.title)}</div><div className="text-[11px] text-[#7a8a99] leading-snug">{t(it.desc)}</div>
          </button>
        );
      })}
    </div>
  );
};

const Erfolg = ({ id, dringend, dateien }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-green-200 rounded-sm p-8 text-center" data-testid="rueckruf-success">
      <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
      <h2 className="font-serif text-2xl text-[#1B3A5C] mb-2">{t('rueckruf.erfolg.titel')}</h2>
      <p className="text-sm text-[#34536b] max-w-md mx-auto">{t('rueckruf.erfolg.beschreibung', { dringend: dringend ? t('rueckruf.erfolg.dringend_zusatz') : '', dateien: dateien ? t('rueckruf.erfolg.dateien_zusatz', { count: dateien }) : '' })}</p>
      <p className="text-xs text-[#9aa5b1] mt-3">{t('rueckruf.erfolg.anfrage_id', { id })}</p>
      <p className="text-xs text-[#7a8a99] mt-4">
        <Trans i18nKey="rueckruf.erfolg.direktkontakt" components={TEL_LINK}>
          Bei laufenden Fristen erreichen Sie uns direkt unter <lnk>+49 228 30 40 640</lnk> (Mo–Fr 9:30–13:00 und 14:00–16:30 Uhr).
        </Trans>
      </p>
    </div>
  );
};

const Rueckruf = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || '', telefon: '', email: user?.email || '', bereich: 'beides', anliegen: '', mandantennummer: '', aktenzeichen: '', erreichbarkeit: [], erreichbarkeit_hinweis: '', dringlichkeit: 'normal', datenschutz: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [dateien, setDateien] = useState([]);
  const addDateien = (list) => {
    const neu = Array.from(list || []);
    const bad = neu.find((x) => !DATEI_EXTS.includes('.' + (x.name.split('.').pop() || '').toLowerCase()));
    if (bad) { toast({ title: t('rueckruf.toast.dateityp_nicht_erlaubt_titel'), description: t('rueckruf.toast.dateityp_nicht_erlaubt_desc', { name: bad.name }), variant: 'destructive' }); return; }
    const big = neu.find((x) => x.size > 15 * 1024 * 1024);
    if (big) { toast({ title: t('rueckruf.toast.datei_zu_gross_titel'), description: t('rueckruf.toast.datei_zu_gross_desc', { name: big.name }), variant: 'destructive' }); return; }
    setDateien((prev) => [...prev, ...neu.filter((x) => !prev.some((p) => p.name === x.name && p.size === x.size))].slice(0, 5));
  };
  const toggleZeit = (k) => set('erreichbarkeit', f.erreichbarkeit.includes(k) ? f.erreichbarkeit.filter((z) => z !== k) : [...f.erreichbarkeit, k]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.datenschutz) { toast({ title: t('rueckruf.toast.datenschutz_bestaetigen'), variant: 'destructive' }); return; }
    if (f.telefon.replace(/\D/g, '').length < 6) { toast({ title: t('rueckruf.toast.telefon_ungueltig'), variant: 'destructive' }); return; }
    setBusy(true);
    try {
      let data;
      if (dateien.length) {
        const fd = new FormData();
        fd.append('payload', JSON.stringify(f));
        dateien.forEach((x) => fd.append('files', x));
        ({ data } = await axios.post(`${API}/rueckruf/mit-dateien`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }));
      } else {
        ({ data } = await axios.post(`${API}/rueckruf`, f));
      }
      setDone(data.rueckruf_id); window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    catch (err) { toast({ title: t('rueckruf.toast.senden_fehlgeschlagen_titel'), description: err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || t('rueckruf.toast.senden_fehlgeschlagen_desc'), variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo title={t('rueckruf.seo.title')} description={t('rueckruf.seo.description')} path="/rueckruf" />
      <Header />
      <main className="pt-[150px]">
        <section className="bg-[#1B3A5C] text-white py-14 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"><div className="absolute top-0 right-0 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div></div>
          <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
            <div className="flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4"><span className="w-12 h-px bg-[#b8893d]"></span>{t('rueckruf.hero.kicker')}</div>
            <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-3">{t('rueckruf.hero.titel')}</h1>
            <p className="text-white/80 leading-relaxed max-w-3xl">{t('rueckruf.hero.beschreibung')}</p>
          </div>
        </section>
        <section className="py-12 bg-[#fafbfc]">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            {done ? <Erfolg id={done} dringend={f.dringlichkeit !== 'normal'} dateien={dateien.length} /> : (
              <form onSubmit={submit} className="space-y-10" data-testid="rueckruf-form">
                <div>
                  <div className={label}>{t('rueckruf.form.schritt_1_label')}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input className={field} placeholder={t('rueckruf.form.name_placeholder')} value={f.name} onChange={(e) => set('name', e.target.value)} required minLength={2} data-testid="rr-name" />
                    <input className={field} type="email" placeholder={t('rueckruf.form.email_placeholder')} value={f.email} readOnly title="E-Mail-Adresse Ihres Kontos" required data-testid="rr-email" />
                    <div className="md:col-span-2">
                      <PhoneInput value={f.telefon} onChange={(v) => set('telefon', v)} placeholder={t('rueckruf.form.telefon_placeholder')} testId="rr-telefon" required />
                    </div>
                  </div>
                </div>
                <div>
                  <div className={label}>{t('rueckruf.form.schritt_2_label')}</div>
                  <TileGroup items={BEREICHE} selected={f.bereich} onPick={(k) => set('bereich', k)} testid="rr-bereich" />
                  <textarea className={`${field} mt-3`} rows={4} placeholder={t('rueckruf.form.anliegen_placeholder')} value={f.anliegen} onChange={(e) => set('anliegen', e.target.value)} required minLength={5} data-testid="rr-anliegen" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <input className={field} placeholder={t('rueckruf.form.mandantennummer_placeholder')} value={f.mandantennummer} onChange={(e) => set('mandantennummer', e.target.value.replace(/\D/g, '').slice(0, 5))} inputMode="numeric" pattern="4\d{4}" title={t('rueckruf.form.mandantennummer_format')} data-testid="rr-mandantennummer" />
                    <input className={field} placeholder={t('rueckruf.form.aktenzeichen_placeholder')} value={f.aktenzeichen} onChange={(e) => set('aktenzeichen', e.target.value)} data-testid="rr-aktenzeichen" />
                  </div>
                  <p className="text-[11px] text-[#7a8a99] mt-2">{t('rueckruf.form.bestandsmandanten_hinweis')}</p>
                </div>
                <div>
                  <div className={label}>{t('rueckruf.form.schritt_3_label')} <span className="text-[#9aa5b1] font-normal normal-case tracking-normal">{t('rueckruf.form.schritt_3_zusatz')}</span></div>
                  <p className="text-[11px] text-[#7a8a99] mb-3">{t('rueckruf.form.burozeiten_hinweis')}</p>
                  <TileGroup items={ZEITEN} selected={f.erreichbarkeit} onPick={toggleZeit} testid="rr-zeit" multi />
                  <input className={`${field} mt-3`} placeholder={t('rueckruf.form.erreichbarkeit_hinweis_placeholder')} value={f.erreichbarkeit_hinweis} onChange={(e) => set('erreichbarkeit_hinweis', e.target.value)} maxLength={300} data-testid="rr-zeit-hinweis" />
                </div>
                <div>
                  <div className={label}>{t('rueckruf.form.schritt_4_label')}</div>
                  <TileGroup items={DRINGLICHKEIT} selected={f.dringlichkeit} onPick={(k) => set('dringlichkeit', k)} testid="rr-dringlichkeit" />
                  {f.dringlichkeit === 'sehr_dringend' && (
                    <div className="mt-3 p-3 bg-[#fff8e1] border-l-2 border-[#b8893d] text-xs text-[#8a6d1a]" data-testid="rr-frist-hinweis">
                      <Trans i18nKey="rueckruf.form.frist_hinweis" components={TEL_LINK_PLAIN}>
                        Läuft heute eine Frist ab? Dann rufen Sie uns bitte zusätzlich direkt an: <lnk>+49 228 30 40 640</lnk>.
                      </Trans>
                    </div>
                  )}
                </div>
                <div>
                  <div className={label}>{t('rueckruf.form.schritt_5_label')} <span className="text-[#9aa5b1] font-normal normal-case tracking-normal">{t('rueckruf.form.schritt_5_zusatz')}</span></div>
                  <label className="flex flex-col items-center justify-center py-6 px-4 bg-white border-2 border-dashed border-gray-300 hover:border-[#b8893d] rounded-sm cursor-pointer transition-colors" data-testid="rr-dropzone"
                    onDrop={(e) => { e.preventDefault(); addDateien(e.dataTransfer.files); }} onDragOver={(e) => e.preventDefault()}>
                    <Paperclip className="w-6 h-6 text-[#b8893d] mb-2" />
                    <span className="text-sm text-[#1B3A5C] font-medium">{t('rueckruf.form.dropzone_titel')}</span>
                    <span className="text-[11px] text-[#7a8a99] mt-1">{t('rueckruf.form.dropzone_beschreibung')}</span>
                    <input type="file" multiple accept={DATEI_EXTS.join(',')} className="hidden" onChange={(e) => { addDateien(e.target.files); e.target.value = ''; }} data-testid="rr-dateien-input" />
                  </label>
                  {dateien.length > 0 && (
                    <ul className="mt-2 divide-y divide-gray-100 border border-gray-100 rounded-sm bg-white" data-testid="rr-dateien-liste">
                      {dateien.map((x, i) => (
                        <li key={x.name + x.size} className="flex items-center gap-2 px-3 py-2 text-sm">
                          <FileText className="w-4 h-4 text-[#b8893d] flex-shrink-0" />
                          <span className="flex-1 truncate text-[#1B3A5C]" title={x.name}>{x.name}</span>
                          <span className="text-[11px] text-[#7a8a99]">{(x.size / 1024).toFixed(0)} KB</span>
                          <button type="button" onClick={() => setDateien((p) => p.filter((_, j) => j !== i))} className="p-1 text-[#7a8a99] hover:text-rose-600" aria-label={t('rueckruf.form.datei_entfernen_aria')} data-testid={`rr-datei-entfernen-${i}`}><X className="w-3.5 h-3.5" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="bg-white border border-gray-100 rounded-sm p-5">
                  <label className="flex items-start gap-3 text-xs text-[#34536b] cursor-pointer">
                    <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#b8893d]" checked={f.datenschutz} onChange={(e) => set('datenschutz', e.target.checked)} data-testid="rr-datenschutz" />
                    <span>
                      <Trans i18nKey="rueckruf.form.datenschutz_text" components={DATENSCHUTZ_LINK}>
                        Ich habe die <lnk>Datenschutzerklärung</lnk> gelesen und stimme der Verarbeitung meiner Angaben zur Bearbeitung der Rückrufanfrage zu. *
                      </Trans>
                    </span>
                  </label>
                  <button type="submit" disabled={busy} data-testid="rr-submit" className="mt-5 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#1B3A5C] hover:bg-[#15304d] disabled:opacity-60 text-white text-xs tracking-[0.2em] px-8 py-4 rounded-sm">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />} {t('rueckruf.form.submit_button')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
      <Toaster />
    </div>
  );
};

export default Rueckruf;
